import { useAtom } from 'jotai';
import type { JobChangeProcess } from '../../types/JobChangeProcess';
import JobChangeProcessNote from './JobChangeProcessNote';
import JobChangeProcessStatusMenu from './JobChangeProcessStatusMenu';
import { currentSelectjobChangeProcessAtom } from '../../atoms/jobChangeProcess';
import clsx from 'clsx';
import DatePickerModal from '@/components/DatePickerModal';
import useUpdateJobChangeProcess from '../../api/useUpdateJobChangeProcess';
import { showErrorToast, showSuccessToast } from '../../lib/toast';
import { datetimeFormat } from '@/utils/dateFormat';
import UsersMenu from '@/features/agent/components/UsersMenu';
import type { ActiveUser } from '../../../types/ActiveUser';

type Props = {
  jobChangeProcess: JobChangeProcess;
  isSidebarOpen: boolean;
  openSidebar: (isValue: boolean) => void;
  statusModalOpen: () => void;
  jobchangeProcessesRefetch: () => Promise<void>;
  activeUsers?: ActiveUser[];
  currentUserId?: number;
  onUpdateCandidateAssignees?: (candidateProfileId: number, userIds: number[]) => void;
};

export default function JobChangeProcessListItem({
  jobChangeProcess,
  isSidebarOpen,
  openSidebar,
  statusModalOpen,
  jobchangeProcessesRefetch,
  activeUsers,
  currentUserId,
  onUpdateCandidateAssignees,
}: Props) {
  const [currentSelectjobChangeProcess, setCurrentSelectjobChangeProcess] = useAtom(
    currentSelectjobChangeProcessAtom
  );
  const { mutateAsync } = useUpdateJobChangeProcess();

  const scrollToTarget = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleDateChange = async (dateString: string) => {
    // クリアボタンが押された場合（空文字列）は何もしない
    if (!dateString) {
      return;
    }

    try {
      const date = new Date(dateString);
      await mutateAsync({
        jobChangeProcessId: jobChangeProcess.id,
        latestLogId: jobChangeProcess.latest_log!.id,
        interviewed_at: date,
      });
      await jobchangeProcessesRefetch();
      scrollToTarget();
      showSuccessToast('選考日程を更新しました');
    } catch (error) {
      showErrorToast('選考日程の更新に失敗しました');
    }
  };

  return (
    <div
      className={clsx(
        'flex min-h-[80px] cursor-pointer items-center justify-around rounded bg-white px-6 shadow-[0px_0px_6px_0px_#00000033] hover:bg-gray',
        currentSelectjobChangeProcess?.id === jobChangeProcess.id && isSidebarOpen && 'bg-gray!'
      )}
      onClick={() => {
        const isOpen = currentSelectjobChangeProcess?.id !== jobChangeProcess.id;
        setCurrentSelectjobChangeProcess(
          isOpen
            ? currentSelectjobChangeProcess?.id === jobChangeProcess.id
              ? null
              : jobChangeProcess
            : null
        );

        openSidebar(isOpen);
      }}
    >
      <div className="flex w-[234px] min-w-[234px] items-center gap-2">
        <p className="text-base font-bold break-all">
          {jobChangeProcess.candidate_profile.name.length >= 8
            ? jobChangeProcess.candidate_profile.name.replace(/(.{8})/g, '$1\n')
            : jobChangeProcess.candidate_profile.name}
        </p>
        {activeUsers && onUpdateCandidateAssignees && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <UsersMenu
              activeUsers={activeUsers}
              selectedUserIds={
                jobChangeProcess.candidate_profile.organization_users?.map((u) => u.id) ?? []
              }
              onSelect={(userIds) =>
                onUpdateCandidateAssignees(jobChangeProcess.candidate_profile.id, userIds)
              }
              currentUserId={currentUserId}
            />
          </div>
        )}
      </div>
      <div className="w-[200px] max-w-[200px] overflow-hidden leading-[1.7]">
        <div>
          <p className="truncate text-xs">
            {jobChangeProcess.company!.name.length > 18
              ? `${jobChangeProcess.company!.name.substring(0, 17)}...`
              : jobChangeProcess.company!.name}
          </p>
          <p className="line-clamp-1 text-xs">
            {jobChangeProcess.provided_job_post!.title.length > 18
              ? `${jobChangeProcess.provided_job_post!.title.substring(0, 17)}...`
              : jobChangeProcess.provided_job_post!.title}
          </p>
        </div>
      </div>
      <div className="w-[180px] max-w-[180px] bg-white">
        <div className="w-[180px]">
          <JobChangeProcessStatusMenu
            statusModalOpen={statusModalOpen}
            jobChangeProcess={jobChangeProcess}
          />
        </div>
      </div>
      <div className="flex w-[176px] max-w-[176px] justify-center">
        {jobChangeProcess.actions.able_to_change_interview_date ? (
          <DatePickerModal
            value={
              jobChangeProcess.latest_log!.interviewed_at
                ? new Date(jobChangeProcess.latest_log!.interviewed_at).toISOString()
                : ''
            }
            onChange={handleDateChange}
            type="datetime"
          >
            {jobChangeProcess.latest_log!.interviewed_at ? (
              <button
                type="button"
                className={clsx(
                  'flex h-[42px] w-full items-center rounded-sm border border-solid border-border bg-white px-3'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <p className={clsx('text-sm text-nowrap text-[#262626]')}>
                  {datetimeFormat(new Date(jobChangeProcess.latest_log!.interviewed_at))}
                </p>
              </button>
            ) : (
              <button
                type="button"
                className={clsx(
                  'flex h-[42px] w-x3l items-center justify-center rounded-sm border border-solid border-border bg-white'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <img
                  src="/agent/job_change_processes/icon-calendar-circle.svg"
                  alt=""
                  width={24}
                  height={24}
                />
              </button>
            )}
          </DatePickerModal>
        ) : (
          jobChangeProcess.actions.able_to_shown_interview_date && (
            <button
              type="button"
              className={clsx(
                'flex h-[42px] w-full items-center rounded-sm border border-solid border-border bg-white px-3'
              )}
            >
              <p className={clsx('text-sm text-nowrap text-[#262626]')}>
                {jobChangeProcess.latest_log!.interviewed_at
                  ? datetimeFormat(new Date(jobChangeProcess.latest_log!.interviewed_at))
                  : 'YYYY/MM/DD 00:00'}
              </p>
            </button>
          )
        )}
      </div>
      <div
        className="relative w-[188px] max-w-[188px] overflow-hidden px-4 py-[10px]"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="relative w-full max-w-full overflow-hidden">
          <JobChangeProcessNote id={jobChangeProcess.id} note={jobChangeProcess.note ?? ''} />
        </div>
      </div>
    </div>
  );
}
